import { useFormik } from "formik";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { usePostCaseCreateMutation } from "../../app/case/api";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { changeNewPatientModal } from "../../app/modal/slice";
import PATH from "../../helpers/PATH";
import yup from "../../helpers/yup";
import {
  ECaseCBCTStatus,
  ECaseTab,
  ECaseTRGStatus,
  ECaseType,
  ECaseView,
} from "../../types/Case";

export const useNewPatientForm = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading: isUserLoading, user } = useAppSelector(
    (state) => state.userSlice
  );
  const [createCase, { isLoading, isSuccess, data }] =
    usePostCaseCreateMutation();

  const formik = useFormik({
    initialValues: {
      caseType: ECaseTab.ORTHODONTICS,
      patientLastName: "",
      patientFirstName: "",
      patientGender: "",
      patientAge: "",
      doctorFullName: "",
      patientNationality: null,
    },
    validationSchema: yup.object().shape({
      caseType: yup
        .string()
        .required(t("form:validation", { context: "requiredField" }) || ""),
      patientLastName: yup
        .string()
        .required(t("form:validation", { context: "requiredField" }) || ""),
      patientFirstName: yup
        .string()
        .required(t("form:validation", { context: "requiredField" }) || ""),
      patientGender: yup.string(),
      patientAge: yup.string(),
      doctorFullName: yup.string(),
      patientNationality: yup.string().nullable(),
    }),
    onSubmit: async (values, { resetForm }) => {
      createCase({
        ...values,
        caseType: values.caseType.toUpperCase(),
      });
    },
  });

  useEffect(() => {
    formik.resetForm();
    if (user) {
      formik.setFieldValue(
        "doctorFullName",
        user.firstName + " " + user.lastName
      );
    }
  }, [i18n.language, user]);

  useEffect(() => {
    if (isSuccess && data && data.id) {
      const specialtySelectionPage = PATH.generate(
        PATH.FP_CASE_SPECIALTY_SELECTION,
        data.id.toString()
      );

      navigate(specialtySelectionPage);
      formik.resetForm();
      dispatch(changeNewPatientModal({ isOpen: false }));
    }
  }, [isSuccess, data]);

  return {
    formik,
    isLoading,
    t,
  };
};
